/**
 * Voice notes: record from the microphone and hand back a 16 kHz mono WAV.
 *
 * MediaRecorder produces webm/opus (mp4/aac on Safari), which the backend would
 * need a codec to read. Decoding here with the Web Audio API and re-encoding to
 * plain PCM keeps the server side free of any audio dependency.
 */

/** Sample rate the speech model works at; resampling here keeps uploads small. */
const TARGET_RATE = 16000;

export class MicrophoneUnavailableError extends Error {}

/** Whether this browser can record at all (needs HTTPS or localhost). */
export const canRecord = (): boolean =>
  typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeString = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM header size
  view.setUint16(20, 1, true); // format: PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeString(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/** Decode whatever the recorder produced, downmix to mono and resample. */
async function toWav(recorded: Blob): Promise<Blob> {
  const ctx = new AudioContext();
  try {
    const decoded = await ctx.decodeAudioData(await recorded.arrayBuffer());
    const frames = Math.ceil((decoded.duration * TARGET_RATE));
    const offline = new OfflineAudioContext(1, Math.max(frames, 1), TARGET_RATE);
    const source = offline.createBufferSource();
    source.buffer = decoded;
    source.connect(offline.destination);
    source.start();
    const rendered = await offline.startRendering();
    return encodeWav(rendered.getChannelData(0), TARGET_RATE);
  } finally {
    void ctx.close();
  }
}

export class VoiceRecorder {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  // Live level for the waveform, read straight from the microphone stream
  private meterCtx: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private samples: Float32Array<ArrayBuffer> | null = null;

  async start(): Promise<void> {
    if (!canRecord()) {
      throw new MicrophoneUnavailableError("L'enregistrement audio n'est pas disponible dans ce navigateur.");
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
    } catch {
      throw new MicrophoneUnavailableError(
        "Micro inaccessible : autorisez l'accès au microphone dans votre navigateur."
      );
    }
    this.chunks = [];
    this.recorder = new MediaRecorder(this.stream);
    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };
    this.recorder.start();
    try {
      this.meterCtx = new AudioContext();
      this.analyser = this.meterCtx.createAnalyser();
      this.analyser.fftSize = 1024;
      this.meterCtx.createMediaStreamSource(this.stream).connect(this.analyser);
      this.samples = new Float32Array(this.analyser.fftSize);
    } catch {
      // No waveform then, the recording itself is unaffected
      this.analyser = null;
    }
  }

  /** Current loudness, 0 (silence) to 1: the RMS of the last few milliseconds. */
  level(): number {
    if (!this.analyser || !this.samples) return 0;
    this.analyser.getFloatTimeDomainData(this.samples);
    let sum = 0;
    for (let i = 0; i < this.samples.length; i++) sum += this.samples[i] * this.samples[i];
    return Math.sqrt(sum / this.samples.length);
  }

  /** Stop recording and return the clip as a 16 kHz mono WAV. */
  async stop(): Promise<Blob> {
    const recorder = this.recorder;
    if (!recorder) throw new Error('Aucun enregistrement en cours.');

    const recorded = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => resolve(new Blob(this.chunks, { type: recorder.mimeType || 'audio/webm' }));
      recorder.stop();
    });
    this.release();
    return toWav(recorded);
  }

  /** Drop the clip and free the microphone (the browser indicator stays on otherwise). */
  cancel(): void {
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.onstop = null;
      this.recorder.stop();
    }
    this.release();
  }

  private release(): void {
    void this.meterCtx?.close();
    this.meterCtx = null;
    this.analyser = null;
    this.samples = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.recorder = null;
    this.chunks = [];
  }
}
